import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import {
  DataEntry,
  DataViewsControllerService,
  NotificationControllerService,
} from '@osos/shared/api';
import {
  DEFAULT_PAGESIZE,
  LoadingIndicator,
  LoadingType,
  ManagedHttpContext,
  NotificationPresenterModel,
  Page,
  ViewImplicitType,
} from '@osos/shared/types';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, delay, finalize, switchMap, tap } from 'rxjs/operators';
import { NotificationFactoryService } from '../../factories';
import {
  ApiUtilsService,
  SocketService,
  ToastService,
  TypesUtilsService,
} from '../../services';
import { SignOut } from '../auth/auth.actions';
import { NotificationActions } from './notification.action';

interface NotificationAppliedFilter {
  skip: number;
  limit: number;
  replace?: boolean;
}
const SECONDS = 30;
const MILLIE_PER_SECOND = 1000;

export interface NotificationStateModel {
  /**
   * temporary state
   */
  page: Page<NotificationPresenterModel>;
  presentedNotifications?: NotificationPresenterModel[];
  appliedFilter: NotificationAppliedFilter;
  unreadCount: number;
  loading: LoadingIndicator;
  newNotifications?: boolean;
  lastFetchedDate?: Date;
  transitoryLastViewLink: string | null;
}

export const defaultNotificationState: NotificationStateModel = {
  page: {
    items: [],
    totalCount: 0,
  },
  presentedNotifications: [],
  appliedFilter: {
    skip: 0,
    limit: DEFAULT_PAGESIZE,
  },
  unreadCount: 0,
  loading: new LoadingIndicator(),
  transitoryLastViewLink: '',
};

@State<NotificationStateModel>({
  name: 'notification',
  defaults: defaultNotificationState,
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class NotificationState {
  private notificationSchedulerSubscription: Subscription | number;
  private countRequestRate = SECONDS * MILLIE_PER_SECOND;

  constructor(
    private apiHelper: ApiUtilsService,
    private notificationService: NotificationControllerService,
    private notificationFactory: NotificationFactoryService,
    private viewsService: DataViewsControllerService,
    private toastService: ToastService,
    private socketService: SocketService,
    private typesUtilsService: TypesUtilsService,
  ) {}

  // --------------------------------- SELECTORS ------------------------------

  @Selector([NotificationState])
  static presentedNotifications(
    state: NotificationStateModel,
  ): NotificationPresenterModel[] | undefined {
    return state.presentedNotifications;
  }

  @Selector([NotificationState])
  static totalCount(state: NotificationStateModel): number {
    return state.page.totalCount;
  }

  @Selector([NotificationState])
  static unreadCount(state: NotificationStateModel): number {
    return state.unreadCount;
  }

  @Selector([NotificationState])
  static newNotification(state: NotificationStateModel): boolean {
    return state.newNotifications ?? false;
  }

  @Selector([NotificationState])
  static transitoryNotificationViewLink(
    state: NotificationStateModel,
  ): string | null {
    return state.transitoryLastViewLink;
  }

  static loading(type: LoadingType) {
    return createSelector(
      [NotificationState],
      (state: NotificationStateModel) => state.loading[type],
    );
  }

  // --------------------------------- Actions ------------------------------
  @Action(NotificationActions.FetchNotificationCount)
  fetchNotificationCount(
    { setState, getState }: StateContext<NotificationStateModel>,
    { payload }: NotificationActions.FetchNotificationCount,
  ): Observable<any> {
    if (document.visibilityState === 'hidden') {
      return of();
    }
    return this.notificationService
      .getMyNotificationCount({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        tap(({ count = 0, unreadCount }) => {
          const oldTotalCount = getState().page.totalCount;
          const pageLoader = getState().loading.page;
          if (count > oldTotalCount) {
            if (!payload?.ignoreNewNotifications) {
              this.toastService.infoMsg(
                'NOTIFICATION.NEW.TITLE',
                'NOTIFICATION.NEW.DETAIL',
                'custom-info-toast',
              );
            }

            // Only set this to true if there is no FetchNotification call in progress
            if (!pageLoader) {
              setState(
                patch<NotificationStateModel>({
                  newNotifications: true,
                  unreadCount,
                  page: patch<Page<NotificationPresenterModel>>({
                    totalCount: count,
                  }),
                }),
              );
            }
          }
        }),
        catchError(() => {
          return of(getState().page.totalCount);
        }),
      );
  }

  @Action(NotificationActions.MarkNotificationAsRead)
  markAsRead(
    { setState, getState }: StateContext<NotificationStateModel>,
    { payload }: NotificationActions.MarkNotificationAsRead,
  ): Observable<any> {
    const { unreadCount } = getState();
    return this.notificationService
      .markAsRead({
        id: payload.id,
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        tap(() => {
          setState(
            patch<NotificationStateModel>({
              presentedNotifications: updateItem<NotificationPresenterModel>(
                (l) => l.id === payload.id,
                patch<NotificationPresenterModel>({
                  readAt: new Date().toISOString(),
                }),
              ),
              unreadCount: unreadCount > 0 ? unreadCount - 1 : 0,
            }),
          );
        }),
      );
  }

  @Action(NotificationActions.FetchNotification)
  fetchTimelinePosts(
    { setState, getState }: StateContext<NotificationStateModel>,
    { payload }: NotificationActions.FetchNotification,
  ): Observable<any> {
    const { skip, limit, replace } = payload;

    // Prepare old results
    const oldPresentedNotifications = replace
      ? []
      : getState().presentedNotifications;
    const { newNotifications } = getState();

    // Prepare filters
    const appliedFilter = getState().appliedFilter;

    const filter = this.prepareNotificationsFilter(
      appliedFilter,
      payload as NotificationAppliedFilter,
    );

    // When request the notification from the first page and there this no new notification,
    // get the old ones
    if (newNotifications === false && replace) {
      return of(oldPresentedNotifications);
    }

    setState(
      patch<NotificationStateModel>({
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );

    // Fetch posts
    return forkJoin([
      this.notificationService.getMyNotifications({
        filter,
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      }),
      this.notificationService.getMyNotificationCount({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      }),
    ]).pipe(
      tap(([entries, { count, unreadCount }]) => {
        const presentedItems = entries.map((entry) => {
          return this.notificationFactory.createPresenterModel(
            entry as DataEntry,
          );
        });
        setState(
          patch<NotificationStateModel>({
            presentedNotifications:
              oldPresentedNotifications?.concat(presentedItems),
            page: patch({ totalCount: count ?? 0 }),
            appliedFilter: patch({
              skip: skip ?? appliedFilter.skip,
              limit: limit ?? appliedFilter.limit,
            }),
            unreadCount,
            newNotifications: false,
            lastFetchedDate: new Date(),
          }),
        );
      }),
      finalize(() => {
        setState(
          patch<NotificationStateModel>({
            loading: patch<LoadingIndicator>({
              page: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(NotificationActions.FetchTransitoryNotificationView)
  navigateToDefaultView(
    { getState, patchState }: StateContext<NotificationStateModel>,
    { payload }: NotificationActions.FetchTransitoryNotificationView,
  ): Observable<any> {
    const { id } = payload;
    const { presentedNotifications } = getState();
    const notification =
      payload.notification ??
      presentedNotifications?.find((notif) => notif.id == id);

    if (notification && notification.reference) {
      return this.viewsService
        .getAViewForEndUser({
          entity: notification.reference.dataEntity,
          entry: notification.reference.dataEntry,
          notificationId: id,
        })
        .pipe(
          tap((data) => {
            if (data?.viewId && data?.entryId) {
              const transitoryLink = this.buildNotificationViewLink(data);
              patchState({
                transitoryLastViewLink: transitoryLink,
              });
            } else {
              this.toastService.warningMsg(
                'message.DONT_HAVE_ACCESS_TO_VIEW',
                true,
              );
              patchState({
                transitoryLastViewLink: null,
              });
            }
          }),
        );
    } else {
      return of({});
    }
  }

  @Action(NotificationActions.ScheduleNotificationFetching)
  scheduleNotificationFetching({
    dispatch,
    getState,
    setState,
  }: StateContext<NotificationStateModel>) {
    dispatch(
      new NotificationActions.FetchNotificationCount({
        ignoreNewNotifications: true,
      }),
    );
    // Connect to socket and subscribe to notifications
    this.notificationSchedulerSubscription = this.socketService
      .connect()
      .pipe(
        delay(100), // Wait for connection to be fully established
        switchMap(() => this.socketService.subscribeToNotifications()),
      )
      .subscribe({
        next: (event) => {
          if (event.type === 'notification') {
            const { unreadCount } = getState();
            setState(
              patch<NotificationStateModel>({
                unreadCount: unreadCount + 1,
                newNotifications: true,
              }),
            );
            const notification = this.notificationFactory.createPresenterModel(
              event.data as DataEntry,
            );
            let title = 'NOTIFICATION.NEW.TITLE';
            if (notification.title) {
              title = this.typesUtilsService.getLocaleString(
                notification.title,
              );
            }
            let detail = 'NOTIFICATION.NEW.DETAIL';
            if (notification.content) {
              detail = this.typesUtilsService.getLocaleString(
                notification.content,
              );
            }

            // Show toast notification
            this.toastService.infoMsg(title, detail, 'custom-info-toast');
          }
        },
        error: (error) => {
          // Fallback to periodic fetching if socket fails
          this.schedulePeriodicFetching(dispatch);
        },
      });
  }

  private schedulePeriodicFetching(dispatch: any) {
    // Fallback to interval-based fetching if socket connection fails
    const countRequestRate = this.countRequestRate * 1000; // 30 seconds

    dispatch(
      new NotificationActions.FetchNotificationCount({
        ignoreNewNotifications: true,
      }),
    );

    this.notificationSchedulerSubscription = setInterval(() => {
      dispatch(new NotificationActions.FetchNotificationCount());
    }, countRequestRate) as unknown as number;
  }

  @Action(SignOut)
  signOut() {
    // Clean up socket subscription
    if (this.notificationSchedulerSubscription) {
      if (
        typeof this.notificationSchedulerSubscription === 'object' &&
        'unsubscribe' in this.notificationSchedulerSubscription
      ) {
        this.notificationSchedulerSubscription.unsubscribe();
      } else {
        // If it's an interval ID
        clearInterval(this.notificationSchedulerSubscription as any);
      }
    }

    // Disconnect socket
    this.socketService.disconnect();
  }

  prepareNotificationsFilter(
    appliedFilter: NotificationAppliedFilter,
    newlyAppliedFilter: NotificationAppliedFilter,
  ): { filter: string } {
    return this.apiHelper.prepareFilter<DataEntry>({
      skip: newlyAppliedFilter.skip ?? appliedFilter.skip,
      limit: newlyAppliedFilter.limit ?? appliedFilter.limit,
      order: ['creationInfo.doneAt DESC'],
    });
  }

  private buildNotificationViewLink(data: {
    viewId: string;
    entityId: string;
    entryId: string;
    viewImplicitType: ViewImplicitType;
  }): string {
    switch (data?.viewImplicitType) {
      case ViewImplicitType.INBOX:
        return `../inbox/${data?.entityId}/r/${data?.entryId}`;
      case ViewImplicitType.MY_REQUEST_DETAILS:
        return `../my/${data?.entityId}/r/${data?.entryId}`;
      default:
        return `../v/${data?.viewId}/entries/${data?.entryId}`;
    }
  }
}
